import { ChevronDown, ChevronRight } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { useState, type ReactNode } from "react";
import { Line, LineChart, Tooltip, XAxis, YAxis } from "recharts";

import { ChartFrame, DataTable, FilterBar } from "@/components/data";
import type { FilterSpec } from "@/components/data";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Skeleton } from "@/components/ui/skeleton";
import { applyFilters, parseFilters } from "@/lib/filters";

import { bandPaint } from "./bands";
import {
  CAMPAIGN_METRICS,
  emailKey,
  getEmailCampaigns,
  getEmailPatterns,
  getEmailSends,
  getEmailSplitTests,
  getEmailSummary,
  getListHealth,
  getTagTree,
  messageFor,
  PATTERN_BYS,
  type CampaignMetric,
  type CampaignTotals,
  type CampaignCell,
  type CampaignSendDetail,
  type EmailCampaign,
  type EmailCampaignsData,
  type EmailPatternGroup,
  type EmailSend,
  type EmailSummary,
  type ListHealthRow,
  type PatternBy,
  SPLIT_METRICS,
  type SplitArm,
  type SplitGroup,
} from "./api";
import {
  CAMPAIGN_METRIC_LABELS,
  CAMPAIGN_NONE,
  campaignCell,
  campaignCompletenessNote,
  campaignHeadline,
  campaignInsights,
  campaignFilterOptions,
  campaignRateTiles,
  cellBand,
  groupSendsByCampaign,
  languageGrid,
  ungroupedNote,
  armBand,
  armLabel,
  comparisonVerdict,
  SPLIT_METRIC_LABELS,
  splitGroupHeadline,
  splitGroupNote,
  splitTrendChart,
  backfillNote,
  channelHeadline,
  EMAIL_FILTER_SCOPE,
  EMAIL_VIEW_LABELS,
  EMAIL_VIEWS,
  parseEmailView,
  type EmailView,
  type RateTile,
  completenessNote,
  emailQuery,
  emailRateTiles,
  emailSendsState,
  evalText,
  funnelText,
  formatCount,
  formatFraction,
  formatWhen,
  campaignWhen,
  sentText,
  espLabel,
  languageLabel,
  sendTypeLabel,
  languageSeries,
  latencyText,
  listHealthHeadline,
  listHealthQuery,
  listStaleLine,
  namespaceLine,
  parseEmailDays,
  parseEmailType,
  EMAIL_TYPE_FILTERS,
  reviewText,
  PATTERN_LABELS,
  patternKeyLabel,
  patternNote,
  sendSourceLabel,
  tagTreeSummary,
  WINDOWS,
  type EmailFilters,
} from "./format";

const DRIP = "Drip data";
const numeric = { meta: { align: "right" as const } };

function SectionError({ title, error, areaName }: { title: string; error: unknown; areaName: string }) {
  return (
    <Alert>
      <AlertTitle>{title} unavailable</AlertTitle>
      <AlertDescription>{messageFor(error, areaName, DRIP)}</AlertDescription>
    </Alert>
  );
}

type EmailControls = EmailFilters & {
  values: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
  patternBy: PatternBy;
  view: EmailView;
};

function useEmailControls(): EmailControls {
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const navigate = useNavigate();
  const values = parseFilters(search);
  const text = (key: string) => (typeof values[key] === "string" && values[key] ? (values[key] as string) : null);
  const by = text("pattern");
  return {
    days: parseEmailDays(values.days),
    language: text("language"),
    audience: text("audience"),
    campaign: text("campaign"),
    sendType: parseEmailType(values.type),
    view: parseEmailView(values.email_view),
    patternBy: (PATTERN_BYS as readonly string[]).includes(by ?? "") ? (by as PatternBy) : "language",
    values,
    onChange: (next) => {
      void navigate({ to: ".", search: applyFilters(search, next as never), replace: true });
    },
  };
}

function EmailFilterBar({
  controls,
  languages,
  audiences,
  campaigns,
  campaignsLoaded,
}: {
  controls: EmailControls;
  languages: string[] | null;
  audiences: string[] | null;
  campaigns: EmailCampaign[] | null;
  campaignsLoaded: boolean;
}) {
  const filters: FilterSpec[] = [
    { name: "days", label: "Window", kind: "choice", options: WINDOWS.map((value) => ({ value, label: `Last ${value} days` })) },
    {
      name: "campaign",
      label: "Campaign",
      kind: "choice",
      anyLabel: "All",
      disabled: campaigns === null,
      options: campaignFilterOptions(campaigns ?? [], controls.campaign ?? null, campaignsLoaded),
    },
    {
      name: "language",
      label: "Language",
      kind: "choice",
      anyLabel: "All",
      disabled: languages === null,
      options: (languages ?? []).map((l) => ({ value: l, label: languageLabel(l) })),
    },
    {
      name: "audience",
      label: "Audience",
      kind: "choice",
      anyLabel: "All",
      disabled: audiences === null,
      options: (audiences ?? []).map((a) => ({ value: a, label: a })),
    },
    {
      name: "type",
      label: "Type",
      kind: "choice",
      anyLabel: "All",
      options: EMAIL_TYPE_FILTERS.map((t) => ({ value: t, label: sendTypeLabel(t) })),
    },
  ];
  return (
    <FilterBar
      layout="inline"
      filters={filters}
      values={{ ...controls.values, days: String(controls.days) } as never}
      onChange={controls.onChange as never}
    />
  );
}

function SourceBadge({ send }: { send: EmailSend }) {
  const note = backfillNote(send);
  return (
    <Badge variant="outline" title={note ?? undefined}>
      {sendSourceLabel(send.source)}
    </Badge>
  );
}

/** One rate tile painted in its KPI band colour: pale fill, solid left edge, band word below. */
function RateTiles({ tiles }: { tiles: RateTile[] }) {
  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-7">
      {tiles.map((tile) => {
        const paint = bandPaint(tile.band);
        return (
          <div
            key={tile.key}
            className="flex flex-col gap-1 rounded-ks-sm border border-ks-grey-4 p-2"
            style={{ background: paint.shade, borderLeft: `4px solid ${paint.solid}` }}
          >
            <p className="text-xs font-semibold text-ks-ink">{tile.label}</p>
            <p className="text-xl font-bold text-ks-ink tabular-nums">{tile.value}</p>
            <p className="flex items-center gap-1 text-xs text-ks-ink">
              <span aria-hidden className="inline-block size-2 rounded-full" style={{ background: paint.solid }} />
              {tile.band ?? "no target set"}
            </p>
            {tile.note && <p className="text-xs text-ks-ink">{tile.note}</p>}
          </div>
        );
      })}
    </div>
  );
}

/** Overview headline: the channel over the filtered window, weighted by CVCE. */
function ChannelSummary({ q, areaName }: { q: string; areaName: string }) {
  const summary = useQuery({ queryKey: emailKey("summary", q), queryFn: () => getEmailSummary(q) });
  if (summary.isPending) return <Skeleton className="h-40 w-full" />;
  if (summary.isError || !summary.data.data.available || !summary.data.data.summary) {
    return <SectionError title="Channel summary" error={summary.error} areaName={areaName} />;
  }
  const s: EmailSummary = summary.data.data.summary;
  return (
    <Card className="border-ks-purple">
      <CardHeader>
        <CardTitle className="text-base">How email is doing in this window</CardTitle>
        <p className="text-sm text-ks-ink">{channelHeadline(s)}</p>
        <p className="text-xs text-ks-ink">Each rate is total events over total delivered across those sends, so a big send counts for more than a small one.</p>
      </CardHeader>
      <CardContent>
        {s.rated_sends === 0 ? (
          <p className="text-sm text-ks-ink">No complete send with 50+ recipients in this window, so no channel rate is shown.</p>
        ) : (
          <RateTiles tiles={emailRateTiles(s)} />
        )}
      </CardContent>
    </Card>
  );
}

const rate = (key: keyof EmailSend, header: string): ColumnDef<EmailSend, unknown> => ({
  id: key,
  header,
  accessorFn: (row) => formatFraction(row[key] as number | null),
  ...numeric,
});

const SEND_COLUMNS: ColumnDef<EmailSend, unknown>[] = [
  { id: "send", header: "Send", accessorFn: (row) => row.broadcast_name ?? row.broadcast_id },
  { id: "date", header: "Sent", accessorFn: (row) => row.send_date },
  { id: "lang", header: "Lang", accessorFn: (row) => languageLabel(row.language) },
  { id: "funnel", header: "Funnel", accessorFn: (row) => funnelText(row) },
  { id: "recipients", header: "Recips", accessorFn: (row) => formatCount(row.recipients), ...numeric },
  rate("open_rate", "Open"),
  rate("click_rate", "CTR"),
  rate("conversion_rate", "CVR"),
  {
    id: "orders",
    header: "Orders",
    accessorFn: (row) => (row.orders_attributed === null || row.orders_attributed === undefined ? "—" : formatCount(row.orders_attributed)),
    ...numeric,
  },
  rate("unsubscribe_rate", "Unsub"),
  rate("reply_rate", "Reply"),
  { id: "eval", header: "Content eval", accessorFn: (row) => evalText(row) },
  { id: "review", header: "Review", accessorFn: (row) => reviewText(row) },
  {
    id: "latency",
    header: "Reply latency",
    accessorFn: (row) => (row.source === "backfill_ui" ? "–" : latencyText(row.median_reply_latency_hours)),
    ...numeric,
  },
  {
    id: "source",
    header: "Source",
    cell: ({ row }) => {
      const note = completenessNote(row.original);
      return (
        <span className="flex flex-wrap gap-1">
          <SourceBadge send={row.original} />
          {row.original.low_sample && <Badge variant="outline">low</Badge>}
          {note && <Badge variant="outline">{note}</Badge>}
        </span>
      );
    },
  },
];

const SERIES_COLOURS = [
  "var(--color-ks-purple)",
  "var(--color-ks-accent)",
  "var(--color-ks-grey-1)",
  "var(--color-ks-purple-3)",
  "var(--color-ks-grey-3)",
];

/** One rate cell: the value always, the band dot only at 100+ recipients. */
function ArmRate({ arm, metric }: { arm: SplitArm; metric: (typeof SPLIT_METRICS)[number] }) {
  const band = armBand(arm, metric);
  const paint = bandPaint(band);
  return (
    <td className="py-1 text-right" title={band ?? (arm.band_masked ? "Under 100 recipients: no band" : "No target set")}>
      <span className="inline-flex items-center justify-end gap-1">
        {band && <span aria-hidden className="inline-block size-2 rounded-full" style={{ background: paint.solid }} />}
        {formatFraction(arm[metric])}
      </span>
    </td>
  );
}

/** One send: its arms (version x cohort), then the A-vs-B verdict per rate, click first. */
function SplitSend({ group }: { group: SplitGroup }) {
  const note = splitGroupNote(group);
  return (
    <Card>
      <CardHeader className="flex flex-col gap-1">
        <CardTitle className="text-base">{splitGroupHeadline(group)}</CardTitle>
        {!group.data_complete && <p className="text-xs text-ks-ink">Some events are still counting or missing, so these numbers can move.</p>}
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <table className="w-full text-sm text-ks-ink">
          <thead>
            <tr className="border-b border-ks-grey-4 text-left">
              <th className="py-1 font-semibold">Version and cohort</th>
              <th className="py-1 text-right font-semibold">Recips</th>
              {SPLIT_METRICS.map((m) => (
                <th key={m} className="py-1 text-right font-semibold">
                  {SPLIT_METRIC_LABELS[m]}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="tabular-nums">
            {group.arms.map((arm) => (
              <tr key={arm.broadcast_id} className="border-b border-ks-grey-4">
                <td className="py-1">{armLabel(arm)}</td>
                <td className="py-1 text-right">{formatCount(arm.recipients)}</td>
                {SPLIT_METRICS.map((m) => (
                  <ArmRate key={m} arm={arm} metric={m} />
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        {group.comparison ? (
          <ul className="flex flex-col gap-1 text-sm text-ks-ink">
            {group.comparison.map((c) => (
              <li key={c.metric} className="flex flex-wrap gap-x-2">
                <span className="font-semibold">{SPLIT_METRIC_LABELS[c.metric]}:</span>
                <span className="tabular-nums">
                  A {formatFraction(c.a_rate)} vs B {formatFraction(c.b_rate)}
                </span>
                <span>· {comparisonVerdict(c)}</span>
              </li>
            ))}
          </ul>
        ) : (
          note && <p className="text-sm text-ks-ink">{note}</p>
        )}
      </CardContent>
    </Card>
  );
}

function SplitTests({ q, areaName }: { q: string; areaName: string }) {
  const split = useQuery({ queryKey: emailKey("split-tests", q), queryFn: () => getEmailSplitTests(q) });
  const data = split.data?.data;
  if (split.isPending) return <Skeleton className="h-40 w-full" />;
  if (split.isError || !data || !data.available) return <SectionError title="Split tests" error={split.error} areaName={areaName} />;
  const trend = splitTrendChart(data.trend);
  return (
    <section className="flex flex-col gap-3">
      <div>
        <h2 className="text-base font-semibold text-ks-ink">Split tests</h2>
        <p className="text-xs text-ks-ink">
          Each send goes to cohort A and cohort B at random, one version each. A result counts as a win only when its 95% range stays on one side of
          zero. Otherwise it reads "no winner yet". Rates under 100 recipients show without a band colour.
        </p>
      </div>
      {data.groups.length === 0 ? (
        <p className="text-sm text-ks-ink">No sends in this range.</p>
      ) : (
        data.groups.map((g) => <SplitSend key={`${g.campaign_key}|${g.language ?? ""}`} group={g} />)
      )}
      {trend.points.length > 0 && (
        <ChartFrame title="Click rate by week, per version and cohort" titleVisible height={220} pointCount={trend.points.length} emptyMessage="No weeks yet.">
          <LineChart data={trend.points} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
            <XAxis dataKey="week" tick={{ fontSize: 10 }} stroke="var(--color-ks-grey-1)" />
            <YAxis tick={{ fontSize: 10 }} stroke="var(--color-ks-grey-1)" width={44} tickFormatter={(v: number) => `${(v * 100).toFixed(1)}%`} />
            <Tooltip
              formatter={(v) => formatFraction(typeof v === "number" ? v : null)}
              contentStyle={{
                background: "var(--color-ks-surface)",
                border: "1px solid var(--color-border)",
                borderRadius: "var(--radius-ks-sm)",
                fontSize: 12,
              }}
            />
            {trend.series.map((s, i) => (
              <Line
                key={s.key}
                type="linear"
                dataKey={s.key}
                name={s.label}
                stroke={SERIES_COLOURS[i % SERIES_COLOURS.length]}
                strokeWidth={2}
                dot={{ r: 2 }}
                connectNulls={false}
                isAnimationActive={false}
              />
            ))}
          </LineChart>
        </ChartFrame>
      )}
    </section>
  );
}

function ListHealth({ days, language, areaName }: { days: EmailControls["days"]; language: string | null; areaName: string }) {
  const q = listHealthQuery(days, language);
  const health = useQuery({ queryKey: emailKey("list-health", q), queryFn: () => getListHealth(q) });
  if (health.isError) return <SectionError title="List health" error={health.error} areaName={areaName} />;
  const data = health.data?.data;
  if (data && !data.available) {
    return <SectionError title="List health" error={null} areaName={areaName} />;
  }
  const points = (data?.days ?? []).map((d) => ({ date: d.snapshot_date, subscribers: d.subscribers }));
  const latestDate = data?.latest_snapshot_date ?? null;
  const latestRows: ListHealthRow[] = (data?.rows ?? []).filter((r) => r.snapshot_date === latestDate);
  const stale = listStaleLine(data?.snapshot_age_days);
  const headline = data ? listHealthHeadline(data.days) : null;
  const first = points[0]?.date;
  return (
    <section className="flex flex-col gap-2">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-base font-semibold text-ks-ink">List health</h2>
        {headline && <p className="text-sm text-ks-ink tabular-nums">{headline}</p>}
      </div>
      {stale && <p className="text-sm font-semibold text-ks-ink">{stale}</p>}
      {health.isPending ? (
        <Skeleton className="h-40 w-full" />
      ) : points.length === 0 ? (
        <p className="text-sm text-ks-ink">No list snapshot in this range.</p>
      ) : (
        <>
          <p className="text-xs text-ks-ink">
            Subscribers per day, one line per language, daily snapshot from {first}. History starts when the snapshot began. A
            missing day is a gap, not a zero.
          </p>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-5">
            {languageSeries(data?.rows ?? []).map((series) => (
              <ChartFrame
                key={series.language ?? "none"}
                title={languageLabel(series.language)}
                titleVisible
                height={120}
                pointCount={series.points.length}
                emptyMessage="No snapshot."
              >
                <LineChart data={series.points} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
                  <XAxis dataKey="date" hide />
                  <YAxis tick={{ fontSize: 10 }} stroke="var(--color-ks-grey-1)" width={44} domain={["auto", "auto"]} />
                  <Tooltip
                    contentStyle={{
                      background: "var(--color-ks-surface)",
                      border: "1px solid var(--color-border)",
                      borderRadius: "var(--radius-ks-sm)",
                      fontSize: 12,
                    }}
                  />
                  <Line
                    type="linear"
                    dataKey="subscribers"
                    name="Subscribers"
                    stroke="var(--color-ks-purple)"
                    strokeWidth={2}
                    dot={{ r: 2, fill: "var(--color-ks-purple)" }}
                    connectNulls={false}
                    isAnimationActive={false}
                  />
                </LineChart>
              </ChartFrame>
            ))}
          </div>
        </>
      )}
      {latestRows.length > 0 && (
        <table className="w-full text-sm text-ks-ink">
          <caption className="pb-1 text-left text-xs">By language on {latestDate}</caption>
          <thead>
            <tr className="border-b border-ks-grey-4 text-left">
              <th className="py-1 font-semibold">Language</th>
              <th className="py-1 text-right font-semibold">Subscribers</th>
              <th className="py-1 text-right font-semibold">New in 7 days</th>
              <th className="py-1 text-right font-semibold">New in 30 days</th>
              <th className="py-1 text-right font-semibold">Prospects</th>
              <th className="py-1 text-right font-semibold">EU consent</th>
            </tr>
          </thead>
          <tbody className="tabular-nums">
            {latestRows.map((r) => (
              <tr key={r.language ?? "none"} className="border-b border-ks-grey-4">
                <td className="py-1">{languageLabel(r.language)}</td>
                <td className="py-1 text-right">{formatCount(r.subscribers)}</td>
                <td className="py-1 text-right">{formatCount(r.new_7d)}</td>
                <td className="py-1 text-right">{formatCount(r.new_30d)}</td>
                <td className="py-1 text-right">{formatCount(r.prospects)}</td>
                <td className="py-1 text-right">{formatCount(r.eu_consent_granted)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <p className="text-xs text-ks-ink">Growth only: unsubscribed people leave the list mirror. Each send's Unsubscribe rate is in the send list.</p>
    </section>
  );
}

const PATTERN_SPEC: FilterSpec = {
  name: "pattern",
  label: "Group by",
  kind: "choice",
  options: PATTERN_BYS.map((by) => ({ value: by, label: PATTERN_LABELS[by] })),
};

function patternColumns(by: PatternBy): ColumnDef<EmailPatternGroup, unknown>[] {
  const avg = (key: keyof EmailPatternGroup, header: string): ColumnDef<EmailPatternGroup, unknown> => ({
    id: key,
    header,
    accessorFn: (row) => (row.too_few_to_compare ? "–" : formatFraction(row[key] as number | null)),
    ...numeric,
  });
  return [
    { id: "key", header: PATTERN_LABELS[by].replace("By ", ""), accessorFn: (row) => patternKeyLabel(by, row.key) },
    { id: "sends", header: "Sends", accessorFn: (row) => formatCount(row.sends), ...numeric },
    avg("avg_open_rate", "Open"),
    avg("avg_click_rate", "CTR"),
    avg("avg_unsubscribe_rate", "Unsub"),
    avg("avg_reply_rate", "Reply"),
    { id: "note", header: "", accessorFn: (row) => patternNote(row) ?? "" },
  ];
}

function Patterns({ q, controls, areaName }: { q: string; controls: EmailControls; areaName: string }) {
  const by = controls.patternBy;
  const groups = useQuery({ queryKey: emailKey(`patterns-${by}`, q), queryFn: () => getEmailPatterns(q, by) });
  const data = groups.data?.data;
  return (
    <section className="flex flex-col gap-2">
      <h2 className="text-base font-semibold text-ks-ink">Content patterns</h2>
      <FilterBar layout="segmented" filters={[PATTERN_SPEC]} values={{ ...controls.values, pattern: by } as never} onChange={controls.onChange as never} />
      {groups.isError || (data && !data.available) ? (
        <SectionError title="Content patterns" error={groups.error} areaName={areaName} />
      ) : (
        <DataTable
          columns={patternColumns(by)}
          rows={data?.groups ?? []}
          pending={groups.isPending}
          page={{ pageIndex: 0, pageSize: Math.max(data?.groups.length ?? 0, 1), rowCount: data?.groups.length ?? 0 }}
          onPageChange={() => {}}
          emptyMessage="No complete sends to compare in this range."
          caption={`Average rates ${PATTERN_LABELS[by].toLowerCase()}`}
        />
      )}
      <p className="text-xs text-ks-ink">Averages of each send's rate, computed by CVCE. Sends still counting or missing events are left out.</p>
    </section>
  );
}

function TagTree({ areaName }: { areaName: string }) {
  const tags = useQuery({ queryKey: emailKey("tags", ""), queryFn: getTagTree });
  const data = tags.data?.data;
  if (tags.isError || (data && !data.available)) return <SectionError title="Tag tree" error={tags.error} areaName={areaName} />;
  if (!data) return <Skeleton className="h-8 w-full" />;
  return (
    <Collapsible className="flex flex-col gap-2">
      <CollapsibleTrigger asChild>
        <Button data-nonmutating variant="ghost" className="justify-start px-0 text-base font-semibold text-ks-ink">
          ▸ Tag tree ({tagTreeSummary(data)})
        </Button>
      </CollapsibleTrigger>
      <CollapsibleContent className="flex flex-col gap-2">
        {data.namespaces.map((ns) =>
          ns.private ? (
            <p key={ns.namespace} className="text-sm text-ks-ink">
              {namespaceLine(ns)}
            </p>
          ) : (
            <Collapsible key={ns.namespace}>
              <CollapsibleTrigger asChild>
                <Button data-nonmutating variant="ghost" size="sm" className="justify-start text-sm text-ks-ink">
                  ▸ {namespaceLine(ns)}
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <ul className="flex flex-col pl-6 text-sm text-ks-ink">
                  {ns.tags.map((t) => (
                    <li key={t.tag} className="flex justify-between gap-2 border-b border-ks-grey-4 py-1">
                      <span>{t.tag}</span>
                      <span className="tabular-nums">{formatCount(t.active_subscribers)} active</span>
                    </li>
                  ))}
                </ul>
              </CollapsibleContent>
            </Collapsible>
          ),
        )}
      </CollapsibleContent>
    </Collapsible>
  );
}

/** One rate with its band dot; the dot is hidden under 100 recipients (CVCE masks the band). */
function BandRate({ totals, metric }: { totals: CampaignTotals | null; metric: CampaignMetric }) {
  const band = cellBand(totals, metric);
  const paint = bandPaint(band);
  return (
    <span className="inline-flex items-center justify-end gap-1" title={band ?? (totals?.band_masked ? "Under 100 recipients: no band" : "No target set")}>
      {band && <span aria-hidden className="inline-block size-2 rounded-full" style={{ background: paint.solid }} />}
      {formatFraction(totals ? totals[metric] : null)}
    </span>
  );
}

function useCampaigns(q: string) {
  return useQuery({ queryKey: emailKey("campaigns", q), queryFn: () => getEmailCampaigns(q) });
}

/** Open rate per language as bars, each painted in its band; striped under 100 recipients. */
function LanguageBars({ campaign }: { campaign: EmailCampaign }) {
  const cells = campaign.languages.map((l) => campaignCell(campaign, l, "first")).filter((c) => c !== null);
  const max = Math.max(...cells.map((c) => c.open_rate ?? 0), campaign.first.open_rate ?? 0, 0.0001);
  const total = campaign.first.open_rate;
  return (
    <div className="flex flex-col gap-1">
      {cells.map((c) => {
        const band = cellBand(c, "open_rate");
        const paint = bandPaint(band);
        return (
          <div key={c.language ?? "none"} className="grid grid-cols-[3rem_1fr_9rem] items-center gap-2 text-sm text-ks-ink">
            <span>{languageLabel(c.language)}</span>
            <div className="relative h-4 overflow-hidden rounded-ks-sm bg-ks-grey-4">
              <div
                className="h-full rounded-ks-sm"
                style={{
                  width: `${((c.open_rate ?? 0) / max) * 100}%`,
                  background: band ? paint.solid : "var(--ks-band-no-target)",
                  opacity: c.band_masked ? 0.55 : 1,
                }}
              />
              {total !== null && <div aria-hidden className="absolute inset-y-0 w-0.5 bg-ks-purple" style={{ left: `${(total / max) * 100}%` }} />}
            </div>
            <span className="text-right text-xs tabular-nums">
              {formatFraction(c.open_rate)} · {formatCount(c.recipients)}
              {c.band_masked && " · small"}
            </span>
          </div>
        );
      })}
      <p className="text-xs text-ks-ink">Purple line = campaign total. A faded bar has under 100 recipients, so one or two people move it a lot.</p>
    </div>
  );
}

function CampaignInsights({ campaign }: { campaign: EmailCampaign }) {
  return (
    <div className="flex flex-col gap-2">
      {campaignInsights(campaign).map((line) => (
        <p key={line} className="border-l-4 border-ks-purple pl-3 text-sm text-ks-ink">
          {line}
        </p>
      ))}
    </div>
  );
}

/** Overview card: the latest (or selected) campaign, all its languages together. */
function LatestCampaign({
  q,
  controls,
  areaName,
  onOpen,
  onOpenLog,
}: {
  q: string;
  controls: EmailControls;
  areaName: string;
  onOpen: (key: string) => void;
  onOpenLog: () => void;
}) {
  const campaigns = useCampaigns(q);
  if (campaigns.isPending) return <Skeleton className="h-40 w-full" />;
  const data = campaigns.data?.data;
  if (campaigns.isError || !data || !data.available) return <SectionError title="Latest campaign" error={campaigns.error} areaName={areaName} />;
  const c = data.campaigns.find((x) => x.campaign_key !== CAMPAIGN_NONE);
  if (!c) {
    const note = ungroupedNote(data.campaigns.find((x) => x.campaign_key === CAMPAIGN_NONE));
    return <p className="text-sm text-ks-ink">{note ? `${note} They are listed in the Sends view.` : "No campaign sends in this range."}</p>;
  }
  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-2">
        <div className="flex flex-col gap-1">
          <CardTitle className="text-base">
            {controls.campaign ? "Selected campaign" : "Latest campaign"}: {c.label}
          </CardTitle>
          <p className="text-sm text-ks-ink">{campaignHeadline(c)}</p>
        </div>
        <div className="flex gap-2">
          <Button data-nonmutating variant="outline" size="sm" onClick={() => onOpen(c.campaign_key)}>
            Open campaign →
          </Button>
          <Button data-nonmutating variant="outline" size="sm" onClick={onOpenLog}>
            Campaign Log →
          </Button>
        </div>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <CampaignCompleteness c={c} />
        <RateTiles tiles={campaignRateTiles(c.first)} />
        <div className="grid gap-4 md:grid-cols-2">
          <LanguageBars campaign={c} />
          <CampaignInsights campaign={c} />
        </div>
      </CardContent>
    </Card>
  );
}

function CampaignList({ data, onOpen }: { data: EmailCampaignsData; onOpen: (key: string) => void }) {
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const grouped = data.campaigns.filter((c) => c.campaign_key !== CAMPAIGN_NONE);
  const none = data.campaigns.find((c) => c.campaign_key === CAMPAIGN_NONE);
  const note = ungroupedNote(none);
  return (
    <section className="flex flex-col gap-2">
      <div>
        <h2 className="text-base font-semibold text-ks-ink">Campaigns</h2>
        <p className="text-xs text-ks-ink">
          One row per campaign. Expand it to see each send on its own row (each wave sent at a different time, or an email's first send and its resend), with its languages inside. Campaign rates are for the first sends; the resend to people who did not open shows on its own.
          "Opened in total" counts people who opened either, over first-send recipients. "Sent" is the send job's own count (SES); a send still going reads "so far".
        </p>
      </div>
      {grouped.length === 0 ? (
        <p className="text-sm text-ks-ink">
          {none ? "No grouped campaigns match. The ungrouped sends are summarised below and listed in the Sends view." : "No campaign sends in this range."}
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-ks-ink">
            <thead>
              <tr className="border-b border-ks-grey-4 text-left">
                <th className="py-1 font-semibold">Campaign</th>
                <th className="py-1 font-semibold">ESP</th>
                <th className="py-1 font-semibold">Type</th>
                <th className="py-1 font-semibold">When</th>
                <th className="py-1 text-right font-semibold">Sent</th>
                <th className="py-1 text-right font-semibold">Recips</th>
                {CAMPAIGN_METRICS.map((m) => (
                  <th key={m} className="py-1 text-right font-semibold">
                    {CAMPAIGN_METRIC_LABELS[m]}
                  </th>
                ))}
                <th className="py-1 text-right font-semibold">Resend open</th>
                <th className="py-1 text-right font-semibold">Opened in total</th>
                <th />
              </tr>
            </thead>
            <tbody className="tabular-nums">
              {grouped.map((c) => (
                <CampaignRows key={c.campaign_key} c={c} expanded={!!open[c.campaign_key]} onToggle={() => setOpen((o) => ({ ...o, [c.campaign_key]: !o[c.campaign_key] }))} onOpen={onOpen} />
              ))}
            </tbody>
          </table>
        </div>
      )}
      {note && <p className="text-xs text-ks-ink">{note}</p>}
    </section>
  );
}

/** Child rows reserve the chevron slot so names line up with their parent (AGENTS.md rule 13). */
function ToggleSpacer() {
  return <span aria-hidden="true" data-toggle-slot className="size-6 shrink-0" />;
}

/**
 * Language rows. Under a campaign the cells come split first / resend; under one send they are that
 * send's own per-language totals (whatever part it was), so the send's numbers show, never dashes.
 */
function LanguageRows({ c, cells, depth, perSend = false }: { c: EmailCampaign; cells: CampaignCell[]; depth: number; perSend?: boolean }) {
  const languages = [...new Set(cells.filter((x) => (perSend || x.part !== "all") && x.language).map((x) => x.language as string))];
  return (
    <>
      {languages.map((l) => {
        const first = perSend
          ? (cells.find((x) => x.language === l && x.part === "all") ?? cells.find((x) => x.language === l) ?? null)
          : (cells.find((x) => x.language === l && x.part === "first") ?? null);
        const resend = perSend ? null : (cells.find((x) => x.language === l && x.part === "resend") ?? null);
        return (
          <tr key={`${c.campaign_key}|${depth}|${l}`} className="border-b border-ks-grey-4 text-xs">
            <td className="py-1">
              <div className="flex items-center gap-2">
                {Array.from({ length: depth }, (_, i) => (
                  <ToggleSpacer key={i} />
                ))}
                <span>{languageLabel(l)}</span>
              </div>
            </td>
            <td />
            <td />
            <td />
            <td />
            <td className="py-1 text-right">{first ? formatCount(first.recipients) : "–"}</td>
            {CAMPAIGN_METRICS.map((m) => (
              <td key={m} className="py-1 text-right">
                <BandRate totals={first} metric={m} />
              </td>
            ))}
            <td className="py-1 text-right">{resend ? formatFraction(resend.open_rate) : "–"}</td>
            <td />
            <td />
          </tr>
        );
      })}
    </>
  );
}

function SendRows({ c, send }: { c: EmailCampaign; send: CampaignSendDetail }) {
  return (
    <>
      <tr className="border-b border-ks-grey-4 text-xs">
        <td className="py-1">
          <div className="flex items-center gap-2">
            <ToggleSpacer />
            <span className="font-semibold">{send.label}</span>
            {send.sending && (
              <Badge variant="outline" className="font-normal">
                Still sending
              </Badge>
            )}
          </div>
        </td>
        <td className="py-1">{espLabel(send.esp)}</td>
        <td className="py-1">{sendTypeLabel(send.send_type)}</td>
        <td className="py-1">{formatWhen(send.send_at) ?? "–"}</td>
        <td className="py-1 text-right">{sentText(send)}</td>
        <td className="py-1 text-right">{formatCount(send.totals.recipients)}</td>
        {CAMPAIGN_METRICS.map((m) => (
          <td key={m} className="py-1 text-right">
            <BandRate totals={send.totals} metric={m} />
          </td>
        ))}
        <td />
        <td />
        <td />
      </tr>
      <LanguageRows c={c} cells={send.cells} depth={2} perSend />
    </>
  );
}

function CampaignRows({ c, expanded, onToggle, onOpen }: { c: EmailCampaign; expanded: boolean; onToggle: () => void; onOpen: (key: string) => void }) {
  const sends = c.sends_detail ?? [];
  return (
    <>
      <tr className="border-b border-ks-grey-4">
        <td className="py-1 font-semibold">
          <div className="flex items-center gap-2">
            <Button
              data-nonmutating
              data-toggle-slot
              variant="ghost"
              size="icon-xs"
              aria-label={`${expanded ? "Collapse" : "Expand"} ${c.label} by send`}
              aria-expanded={expanded}
              onClick={onToggle}
            >
              {expanded ? <ChevronDown /> : <ChevronRight />}
            </Button>
            <span>{c.label}</span>
            {c.sending ? (
              <Badge variant="outline" className="font-normal">
                Still sending
              </Badge>
            ) : (
              !c.data_complete && (
                <Badge variant="outline" className="font-normal">
                  Still counting
                </Badge>
              )
            )}
          </div>
        </td>
        <td className="py-1">{espLabel(c.esp)}</td>
        <td className="py-1">{sendTypeLabel(c.send_type)}</td>
        <td className="py-1">{campaignWhen(c)}</td>
        <td className="py-1 text-right">{sentText(c)}</td>
        <td className="py-1 text-right">{formatCount(c.first.recipients)}</td>
        {CAMPAIGN_METRICS.map((m) => (
          <td key={m} className="py-1 text-right">
            <BandRate totals={c.first} metric={m} />
          </td>
        ))}
        <td className="py-1 text-right">{c.resend ? formatFraction(c.resend.open_rate) : "–"}</td>
        <td className="py-1 text-right font-semibold">{formatFraction(c.resend ? c.opened_in_total_rate : c.first.open_rate)}</td>
        <td className="py-1 text-right">
          <Button data-nonmutating variant="outline" size="sm" onClick={() => onOpen(c.campaign_key)}>
            Open →
          </Button>
        </td>
      </tr>
      {expanded &&
        (sends.length > 0
          ? sends.map((send) => <SendRows key={`${c.campaign_key}|${send.send_key}`} c={c} send={send} />)
          : <LanguageRows c={c} cells={c.cells} depth={1} />)}
    </>
  );
}

function CampaignCompleteness({ c }: { c: EmailCampaign }) {
  const note = campaignCompletenessNote(c);
  return note ? <p className="text-xs text-ks-ink">{note}</p> : null;
}

function CampaignDetail({ c, onBack }: { c: EmailCampaign; onBack: () => void }) {
  return (
    <section className="flex flex-col gap-3">
      <p className="text-sm text-ks-ink">
        <button data-nonmutating type="button" className="text-ks-purple underline" onClick={onBack}>
          All campaigns
        </button>{" "}
        › <span className="font-semibold">{c.label}</span>
      </p>
      <Card className="border-ks-purple">
        <CardHeader>
          <CardTitle className="text-base">{c.label}</CardTitle>
          <p className="text-sm text-ks-ink">{campaignHeadline(c)}</p>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          <CampaignCompleteness c={c} />
          <RateTiles tiles={campaignRateTiles(c.first)} />
          {c.resend && (
            <p className="text-sm text-ks-ink">
              Opened in total (first send or resend): <span className="font-semibold tabular-nums">{formatFraction(c.opened_in_total_rate)}</span>
            </p>
          )}
        </CardContent>
      </Card>
      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Open rate by language</CardTitle>
          </CardHeader>
          <CardContent>
            <LanguageBars campaign={c} />
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-base">What to read from it</CardTitle>
          </CardHeader>
          <CardContent>
            <CampaignInsights campaign={c} />
          </CardContent>
        </Card>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm text-ks-ink">
          <caption className="pb-1 text-left text-xs">By language. Under 100 recipients the value shows without a band colour.</caption>
          <thead>
            <tr className="border-b border-ks-grey-4 text-left">
              <th className="py-1 font-semibold">Language</th>
              <th className="py-1 text-right font-semibold">Recips</th>
              {CAMPAIGN_METRICS.map((m) => (
                <th key={m} className="py-1 text-right font-semibold">
                  {CAMPAIGN_METRIC_LABELS[m]}
                </th>
              ))}
              <th className="py-1 text-right font-semibold">Replies</th>
              <th className="py-1 text-right font-semibold">Resend open</th>
            </tr>
          </thead>
          <tbody className="tabular-nums">
            {c.languages.map((l) => {
              const first = campaignCell(c, l, "first");
              const resend = campaignCell(c, l, "resend");
              return (
                <tr key={l} className="border-b border-ks-grey-4">
                  <td className="py-1">{languageLabel(l)}</td>
                  <td className="py-1 text-right">{first ? formatCount(first.recipients) : "–"}</td>
                  {CAMPAIGN_METRICS.map((m) => (
                    <td key={m} className="py-1 text-right">
                      <BandRate totals={first} metric={m} />
                    </td>
                  ))}
                  <td className="py-1 text-right">{first ? formatCount(first.replies) : "–"}</td>
                  <td className="py-1 text-right">{resend ? formatFraction(resend.open_rate) : "–"}</td>
                </tr>
              );
            })}
            <tr className="font-semibold">
              <td className="py-1">Campaign total</td>
              <td className="py-1 text-right">{formatCount(c.first.recipients)}</td>
              {CAMPAIGN_METRICS.map((m) => (
                <td key={m} className="py-1 text-right">
                  <BandRate totals={c.first} metric={m} />
                </td>
              ))}
              <td className="py-1 text-right">{formatCount(c.first.replies)}</td>
              <td className="py-1 text-right">{c.resend ? formatFraction(c.resend.open_rate) : "–"}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  );
}

function Campaigns({ q, controls, areaName, onOpen }: { q: string; controls: EmailControls; areaName: string; onOpen: (key: string | null) => void }) {
  const campaigns = useCampaigns(q);
  if (campaigns.isPending) return <Skeleton className="h-40 w-full" />;
  const data = campaigns.data?.data;
  if (campaigns.isError || !data || !data.available) return <SectionError title="Campaigns" error={campaigns.error} areaName={areaName} />;
  const selected = controls.campaign && controls.campaign !== CAMPAIGN_NONE ? data.campaigns.find((c) => c.campaign_key === controls.campaign) : undefined;
  return selected ? <CampaignDetail c={selected} onBack={() => onOpen(null)} /> : <CampaignList data={data} onOpen={onOpen} />;
}

const GRID_METRIC_SPEC: FilterSpec = {
  name: "grid_metric",
  label: "Rate",
  kind: "choice",
  options: CAMPAIGN_METRICS.map((m) => ({ value: m, label: CAMPAIGN_METRIC_LABELS[m] })),
};

function GridCell({ totals, metric }: { totals: CampaignTotals | null; metric: CampaignMetric }) {
  if (!totals) return <td className="border-2 border-ks-surface p-1 text-center">–</td>;
  const band = cellBand(totals, metric);
  const paint = bandPaint(band);
  return (
    <td
      className="border-2 border-ks-surface p-1 text-center tabular-nums"
      style={{ background: band ? paint.shade : undefined, boxShadow: band ? `inset 0 -3px 0 ${paint.solid}` : undefined }}
      title={band ?? (totals.band_masked ? "Under 100 recipients: no band" : "No target set")}
    >
      {formatFraction(totals[metric])}
      <span className="block text-xs">
        {formatCount(totals.recipients)}
        {totals.band_masked && " · small"}
      </span>
    </td>
  );
}

/** Campaign x language grid. Campaign and language filters do not reach it: it is the whole grid. */
function LanguagesGrid({ q, controls, areaName, onOpen }: { q: string; controls: EmailControls; areaName: string; onOpen: (key: string) => void }) {
  const [withResend, setWithResend] = useState(false);
  const campaigns = useCampaigns(q);
  const raw = typeof controls.values.grid_metric === "string" ? controls.values.grid_metric : "";
  const metric: CampaignMetric = (CAMPAIGN_METRICS as readonly string[]).includes(raw) ? (raw as CampaignMetric) : "open_rate";
  if (campaigns.isPending) return <Skeleton className="h-40 w-full" />;
  const data = campaigns.data?.data;
  if (campaigns.isError || !data || !data.available) return <SectionError title="Languages" error={campaigns.error} areaName={areaName} />;
  const grid = languageGrid(data, withResend ? "all" : "first");
  return (
    <section className="flex flex-col gap-2">
      <div>
        <h2 className="text-base font-semibold text-ks-ink">Campaign × language</h2>
        <p className="text-xs text-ks-ink">
          Each cell is one language version of one campaign. Read across a row to compare languages; read down a column to see whether a language is
          strong or weak across emails. Under 100 recipients a cell shows no band colour.
        </p>
      </div>
      <FilterBar layout="segmented" filters={[GRID_METRIC_SPEC]} values={{ ...controls.values, grid_metric: metric } as never} onChange={controls.onChange as never} />
      <label className="flex items-center gap-2 text-sm text-ks-ink">
        <input data-nonmutating type="checkbox" checked={withResend} onChange={(e) => setWithResend(e.target.checked)} />
        Add the resend to unopened into each cell
      </label>
      {grid.rows.length === 0 ? (
        <p className="text-sm text-ks-ink">No campaign sends in this range.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-ks-ink">
            <thead>
              <tr className="border-b border-ks-grey-4">
                <th className="py-1 text-left font-semibold">Campaign</th>
                {grid.languages.map((l) => (
                  <th key={l ?? "none"} className="py-1 font-semibold">
                    {languageLabel(l)}
                  </th>
                ))}
                <th className="py-1 font-semibold">All languages</th>
                {!withResend && <th className="py-1 font-semibold">All but English</th>}
              </tr>
            </thead>
            <tbody>
              {grid.rows.map((row) => (
                <tr key={row.key}>
                  <td className="py-1">
                    <button data-nonmutating type="button" className="text-left text-ks-purple underline" onClick={() => onOpen(row.key)}>
                      {row.label}
                    </button>
                  </td>
                  {row.cells.map((cell, i) => (
                    <GridCell key={grid.languages[i] ?? "none"} totals={cell} metric={metric} />
                  ))}
                  <GridCell totals={row.total} metric={metric} />
                  {!withResend && <GridCell totals={row.nonEnglish} metric={metric} />}
                </tr>
              ))}
              <tr className="font-semibold">
                <td className="py-1">All campaigns</td>
                {grid.footer.map((cell, i) => (
                  <GridCell key={grid.languages[i] ?? "none"} totals={cell} metric={metric} />
                ))}
                <td />
                {!withResend && <td />}
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

/** Sends folded under their campaign; each header carries CVCE's first-send total (the same basis as the Campaigns view). */
function GroupedSends({ sends, q }: { sends: EmailSend[]; q: string }) {
  const campaigns = useCampaigns(q);
  const list = campaigns.data?.data.available ? campaigns.data.data.campaigns : [];
  const groups = groupSendsByCampaign(sends, list);
  return (
    <section className="flex flex-col gap-3">
      <h2 className="text-base font-semibold text-ks-ink">Sends, grouped by campaign</h2>
      {groups.map((g) => (
        <SendGroup key={g.key} g={g} />
      ))}
      <p className="text-xs text-ks-ink">
        Streamed rows come from Drip's webhook, event by event. Backfilled rows were copied from Drip's report and carry no reply latency.
      </p>
    </section>
  );
}

/** One campaign's sends; the arrow tracks whether the group is open. */
function SendGroup({ g }: { g: ReturnType<typeof groupSendsByCampaign>[number] }) {
  const [open, setOpen] = useState(true);
  return (
    <Collapsible open={open} onOpenChange={setOpen} className="flex flex-col gap-1">
      <CollapsibleTrigger asChild>
        <Button data-nonmutating variant="ghost" className="justify-between px-0 text-sm font-semibold text-ks-ink">
          <span>
            {open ? "▾" : "▸"} {g.label} · {g.sends.length} {g.sends.length === 1 ? "send" : "sends"}
          </span>
          {g.campaign && (
            <span className="text-xs font-normal tabular-nums">
              First send: {formatCount(g.campaign.first.recipients)} recipients · open {formatFraction(g.campaign.first.open_rate)} · CTR{" "}
              {formatFraction(g.campaign.first.click_rate)}
            </span>
          )}
        </Button>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <DataTable
          columns={SEND_COLUMNS}
          rows={g.sends}
          page={{ pageIndex: 0, pageSize: Math.max(g.sends.length, 1), rowCount: g.sends.length }}
          onPageChange={() => {}}
          emptyMessage="No sends."
          caption={`${g.label}: every send, newest first`}
        />
      </CollapsibleContent>
    </Collapsible>
  );
}

/**
 * Email (UX S12 / W8, split into views after Damien's live review 2026-09-27):
 * Overview (the channel over the window, then the latest send), Sends, List
 * health, Patterns, Tags. One filter bar drives every view; each view states
 * which filters reach it. Each section fails on its own.
 */
export function EmailTab({ areaName }: { areaName: string }) {
  const controls = useEmailControls();
  const navigate = useNavigate();
  const params = useParams({ strict: false }) as Record<string, string | undefined>;
  const q = emailQuery(controls);
  const sends = useQuery({ queryKey: emailKey("sends", q), queryFn: () => getEmailSends(q) });
  // Filter options come from CVCE's own groups over the window, not from the rows on screen.
  const windowOnly = emailQuery({ days: controls.days, language: null, audience: null });
  const campaignOptions = useCampaigns(windowOnly);
  const languages = useQuery({ queryKey: emailKey("patterns-language", windowOnly), queryFn: () => getEmailPatterns(windowOnly, "language") });
  const audiences = useQuery({ queryKey: emailKey("patterns-audience", windowOnly), queryFn: () => getEmailPatterns(windowOnly, "audience") });
  const keys = (d: typeof languages.data) =>
    d?.data.available ? d.data.groups.map((g) => g.key).filter((k): k is string => Boolean(k)) : null;

  const openCampaign = (key: string | null) => controls.onChange({ ...controls.values, campaign: key ?? undefined, email_view: "campaigns" });

  const openLog = () =>
    void navigate({
      to: "/app/$area/$module/$tab",
      params: { area: params.area ?? "customer_acquisition", module: params.module ?? "cvce_marketing", tab: "campaign_log" },
    } as never);

  const data = sends.data?.data;
  const state = data ? emailSendsState(data, controls) : null;
  const view = controls.view;
  const VIEW_SPEC: FilterSpec = {
    name: "email_view",
    label: "View",
    kind: "choice",
    options: EMAIL_VIEWS.map((v) => ({ value: v, label: EMAIL_VIEW_LABELS[v] })),
  };

  const sendsSection = (render: (sends: EmailSend[]) => ReactNode) =>
    sends.isPending ? (
      <Skeleton className="h-40 w-full" />
    ) : sends.isError ? (
      <SectionError title="Send scorecard" error={sends.error} areaName={areaName} />
    ) : state ? (
      <p className="text-sm text-ks-ink">{state}</p>
    ) : (
      render(data!.sends)
    );

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h2 className="text-lg font-semibold text-ks-ink">Email</h2>
        <p className="text-sm text-ks-ink">Send outcomes and list health from Drip. Sends update hourly; the list snapshot runs daily at 05:30.</p>
      </div>
      <FilterBar layout="segmented" filters={[VIEW_SPEC]} values={{ ...controls.values, email_view: view } as never} onChange={controls.onChange as never} />
      <EmailFilterBar
        controls={controls}
        languages={languages.isPending ? [] : keys(languages.data)}
        audiences={audiences.isPending ? [] : keys(audiences.data)}
        campaigns={campaignOptions.isPending ? [] : campaignOptions.data?.data.available ? campaignOptions.data.data.campaigns : null}
        campaignsLoaded={!campaignOptions.isPending && !!campaignOptions.data?.data.available}
      />
      <p className="text-xs text-ks-ink">Filters on this view: {EMAIL_FILTER_SCOPE[view]}</p>
      {view === "overview" && (
        <>
          <ChannelSummary q={q} areaName={areaName} />
          <LatestCampaign q={q} controls={controls} areaName={areaName} onOpen={openCampaign} onOpenLog={openLog} />
        </>
      )}
      {view === "campaigns" && <Campaigns q={q} controls={controls} areaName={areaName} onOpen={openCampaign} />}
      {view === "languages" && <LanguagesGrid q={emailQuery({ days: controls.days, language: null, audience: controls.audience, sendType: controls.sendType })} controls={controls} areaName={areaName} onOpen={openCampaign} />}
      {view === "sends" && sendsSection((rows) => <GroupedSends sends={rows} q={q} />)}
      {view === "split" && <SplitTests q={q} areaName={areaName} />}
      {view === "list" && <ListHealth days={controls.days} language={controls.language} areaName={areaName} />}
      {view === "patterns" && <Patterns q={q} controls={controls} areaName={areaName} />}
      {view === "tags" && <TagTree areaName={areaName} />}
    </div>
  );
}
